import { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, IndexTable, Tabs, Text, TextField } from "@shopify/polaris";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import agent from "../../app/api/agent";
import { formatLocalDateTime } from "./utils/dateTime";
import { AdminDataFeedRunLog, AdminDataFeedStoreSummary, AdminPagedResult } from "../../app/models/Admin";

type AdminConsoleContext = {
  token: string;
  onUnauthorized: () => void;
};

const pageSize = 50;

export default function AdminDataFeedPage() {
  const { token, onUnauthorized } = useOutletContext<AdminConsoleContext>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedTab, setSelectedTab] = useState(0);

  const tabs = useMemo(() => [
    { id: "sync-logs", content: "Sync logs" },
    { id: "stores", content: "Stores" }
  ], []);

  useEffect(() => {
    const tab = searchParams.get("tab");
    setSelectedTab(tab === "stores" ? 1 : 0);
  }, [searchParams]);

  const handleTabChange = (index: number) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", index === 1 ? "stores" : "sync-logs");
    if (index === 1) {
      next.delete("page");
      next.delete("shopId");
      next.delete("status");
    }
    setSearchParams(next);
  };

  return (
    <Card>
      <div style={{ display: "grid", gap: "0.75rem" }}>
        <Text as="h3" variant="headingMd">Data Feed</Text>
        <Tabs tabs={tabs} selected={selectedTab} onSelect={handleTabChange} />
        {selectedTab === 0 ? (
          <DataFeedSyncLogsTab token={token} onUnauthorized={onUnauthorized} navigate={navigate} searchParams={searchParams} setSearchParams={setSearchParams} />
        ) : (
          <DataFeedStoresTab token={token} onUnauthorized={onUnauthorized} navigate={navigate} />
        )}
      </div>
    </Card>
  );
}

function DataFeedSyncLogsTab({ token, onUnauthorized, navigate, searchParams, setSearchParams }: any) {
  const [result, setResult] = useState<AdminPagedResult<AdminDataFeedRunLog> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const page = Math.max(1, Number(searchParams.get("page") || "1") || 1);
  const shopIdValue = searchParams.get("shopId") || "";
  const status = searchParams.get("status") || "";

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError(null);

    const parsedShopId = shopIdValue ? Number(shopIdValue) : undefined;

    try {
      const response = await agent.Admin.dataFeedLogs(token, page, pageSize, Number.isFinite(parsedShopId) ? parsedShopId : undefined, status || undefined);
      setResult(response);
    } catch {
      setError("Unable to load data feed sync logs.");
      onUnauthorized();
    } finally {
      setLoading(false);
    }
  }, [token, page, shopIdValue, status, onUnauthorized]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  const totalPages = result ? Math.max(1, Math.ceil(result.totalCount / result.pageSize)) : 1;
  const setPage = (nextPage: number) => {
    const next = new URLSearchParams(searchParams);
    next.set("page", String(nextPage));
    setSearchParams(next);
  };

  const updateFilter = (updates: Record<string, string>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (!value) next.delete(key); else next.set(key, value);
    });
    next.set("page", "1");
    setSearchParams(next);
  };

  return <div style={{ display: "grid", gap: "0.75rem" }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <Text as="h4" variant="headingSm">Data feed sync logs</Text>
      <Button onClick={loadLogs} loading={loading}>Refresh logs</Button>
    </div>
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
      <TextField label="Filter by Shop ID" value={shopIdValue} onChange={(value) => updateFilter({ shopId: value })} autoComplete="off" />
      <TextField label="Filter by status" value={status} onChange={(value) => updateFilter({ status: value })} autoComplete="off" />
    </div>
    {error ? <Text as="p" tone="critical">{error}</Text> : null}
    {loading ? <Text as="p">Loading data feed logs...</Text> : !result || result.items.length === 0 ? <Text as="p">No data feed logs found.</Text> : (
      <IndexTable resourceName={{ singular: "data feed log", plural: "data feed logs" }} itemCount={result.items.length} selectable={false}
        headings={[{ title: "ID" }, { title: "Store" }, { title: "Profile" }, { title: "Run Type" }, { title: "Status" }, { title: "Started" }, { title: "Ended" }]}>
        {result.items.map((row, index) => (
          <IndexTable.Row id={`${row.id}`} key={row.id} position={index}>
            <IndexTable.Cell>{row.id}</IndexTable.Cell>
            <IndexTable.Cell><Button variant="plain" onClick={() => navigate(`/pf-console/data-feed/stores/${row.shopId}`)}>{row.shopName || row.shopDomain || `Shop ${row.shopId}`}</Button></IndexTable.Cell>
            <IndexTable.Cell>{row.profileName || (row.dataFeedProfileId ? `#${row.dataFeedProfileId}` : "-")}</IndexTable.Cell>
            <IndexTable.Cell>{row.runType || "-"}</IndexTable.Cell>
            <IndexTable.Cell><Badge tone={(row.status || "").toLowerCase().includes("success") ? "success" : ((row.status || "").toLowerCase().includes("fail") ? "critical" : "attention")}>{row.status || "-"}</Badge></IndexTable.Cell>
            <IndexTable.Cell>{formatLocalDateTime(row.startedAt)}</IndexTable.Cell>
            <IndexTable.Cell>{formatLocalDateTime(row.endedAt)}</IndexTable.Cell>
          </IndexTable.Row>
        ))}
      </IndexTable>
    )}
    <div style={{ display: "flex", justifyContent: "space-between" }}>
      <Button disabled={page <= 1 || loading} onClick={() => setPage(page - 1)}>Previous</Button>
      <Text as="span">Page {page} of {totalPages}</Text>
      <Button disabled={page >= totalPages || loading} onClick={() => setPage(page + 1)}>Next</Button>
    </div>
  </div>;
}

function DataFeedStoresTab({ token, onUnauthorized, navigate }: any) {
  const [stores, setStores] = useState<AdminDataFeedStoreSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shopIdInput, setShopIdInput] = useState("");

  const loadStores = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setStores(await agent.Admin.dataFeedStores(token));
    } catch {
      setError("Unable to load stores with data feed profiles.");
      onUnauthorized();
    } finally {
      setLoading(false);
    }
  }, [token, onUnauthorized]);

  useEffect(() => { loadStores(); }, [loadStores]);

  const openStore = () => {
    const parsedShopId = Number(shopIdInput);
    if (!Number.isFinite(parsedShopId) || parsedShopId <= 0) {
      setError("Enter a valid Shop ID.");
      return;
    }

    navigate(`/pf-console/data-feed/stores/${parsedShopId}`);
  };

  return <div style={{ display: "grid", gap: "0.75rem" }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <Text as="h4" variant="headingSm">Stores with existing data feed profiles</Text>
      <Button onClick={loadStores} loading={loading}>Refresh stores</Button>
    </div>
    <div style={{ display: "grid", gridTemplateColumns: "minmax(12rem, 18rem) auto", gap: "0.75rem", alignItems: "end", justifyContent: "start" }}>
      <TextField label="Open store by Shop ID" value={shopIdInput} onChange={setShopIdInput} autoComplete="off" />
      <Button onClick={openStore}>Open store</Button>
    </div>
    {error ? <Text as="p" tone="critical">{error}</Text> : null}
    {loading ? <Text as="p">Loading stores...</Text> : stores.length === 0 ? <Text as="p">No stores found.</Text> : (
      <IndexTable resourceName={{ singular: "store", plural: "stores" }} itemCount={stores.length} selectable={false}
        headings={[{ title: "Store" }, { title: "Domain" }, { title: "Profiles" }, { title: "Enabled" }]}>
        {stores.map((store, index) => (
          <IndexTable.Row id={`${store.shopId}`} key={store.shopId} position={index}>
            <IndexTable.Cell><Button variant="plain" onClick={() => navigate(`/pf-console/data-feed/stores/${store.shopId}`)}>{store.shopName || `Shop ${store.shopId}`}</Button></IndexTable.Cell>
            <IndexTable.Cell>{store.shopDomain || "-"}</IndexTable.Cell>
            <IndexTable.Cell>{store.profileCount}</IndexTable.Cell>
            <IndexTable.Cell>{store.enabledProfileCount}</IndexTable.Cell>
          </IndexTable.Row>
        ))}
      </IndexTable>
    )}
  </div>;
}
